import React, { useState, useEffect } from 'react';
import './HiFiReview.css';

const HiFiReviewForm = ({ onReviewSubmitted, onCancel, editReview = null }) => {
  const [formData, setFormData] = useState({
    manufacturer: '',
    category_id: '',
    component: '',
    rating: 0,
    is_recommended: null,
    price_performance: null, // Neues Feld für Preis-Leistung
    review_text: '',
    hifi_chain: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const [chainCharCount, setChainCharCount] = useState(0);
  const MAX_REVIEW_LENGTH = 900; // Neue Zeichenbegrenzung
  const MAX_CHAIN_LENGTH = 200;

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Modifiziert, um alle Kategorien zu holen, nicht nur Hauptkategorien
        const response = await fetch('/api/marketplace/categories');
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Kategorien');
        }
        
        const data = await response.json();
        setCategories(data); // Alle Kategorien, inkl. Unterkategorien
      } catch (error) {
        console.error('Error fetching categories:', error);
        setErrors(prev => ({ ...prev, submit: 'Fehler beim Laden der Kategorien.' }));
      }
    };
    
    fetchCategories();
    
    // If in edit mode, populate form with review data
    if (editReview) {
      setFormData({
        manufacturer: editReview.manufacturer || '',
        category_id: editReview.category_id || '',
        component: editReview.component || '',
        rating: editReview.rating || 0,
        is_recommended: editReview.is_recommended,
        price_performance: editReview.price_performance || null,
        review_text: editReview.review_text || '',
        hifi_chain: editReview.hifi_chain || ''
      });
      setCharCount(editReview.review_text ? editReview.review_text.length : 0);
      setChainCharCount(editReview.hifi_chain ? editReview.hifi_chain.length : 0);
    }
  }, [editReview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'review_text') {
      // Handle text with character limit
      if (value.length <= MAX_REVIEW_LENGTH) {
        setFormData({
          ...formData,
          [name]: value
        });
        setCharCount(value.length);
      }
    } else if (name === 'hifi_chain') {
      // Handle HiFi chain text with character limit
      if (value.length <= MAX_CHAIN_LENGTH) {
        setFormData({
          ...formData,
          [name]: value
        });
        setChainCharCount(value.length);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleStarClick = (rating) => {
    setFormData({
      ...formData,
      rating
    });
    
    if (errors.rating) {
      setErrors({
        ...errors,
        rating: null
      });
    }
  };

  const handleThumbClick = (isRecommended) => {
    setFormData({
      ...formData,
      is_recommended: isRecommended
    });
    
    if (errors.is_recommended) {
      setErrors({
        ...errors,
        is_recommended: null
      });
    }
  };

  // Neuer Handler für Preis-Leistungs-Verhältnis
  const handlePricePerformanceClick = (value) => {
    setFormData({
      ...formData,
      price_performance: value
    });
    
    if (errors.price_performance) {
      setErrors({
        ...errors,
        price_performance: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.manufacturer.trim()) {
      newErrors.manufacturer = 'Hersteller ist erforderlich';
    }
    
    if (!formData.category_id) {
      newErrors.category_id = 'Kategorie ist erforderlich';
    }
    
    if (!formData.component.trim()) {
      newErrors.component = 'Komponente ist erforderlich';
    }
    
    if (formData.rating === 0) {
      newErrors.rating = 'Bewertung ist erforderlich';
    }
    
    if (formData.is_recommended === null) {
      newErrors.is_recommended = 'Empfehlung ist erforderlich';
    }

    if (formData.price_performance === null) {
      newErrors.price_performance = 'Preis-Leistungs-Bewertung ist erforderlich';
    }

    // Falls es hier eine Validierung für die Textlänge gibt
    if (formData.review_text && formData.review_text.length > MAX_REVIEW_LENGTH) {
      newErrors.review_text = `Bewertungstext darf nicht mehr als ${MAX_REVIEW_LENGTH} Zeichen enthalten.`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const method = editReview ? 'PUT' : 'POST';
      const url = editReview 
        ? `/api/reviews/${editReview.id}`
        : '/api/reviews';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Fehler beim ${editReview ? 'Aktualisieren' : 'Speichern'} der Bewertung`);
      }
      
      const data = await response.json();
      onReviewSubmitted(data, editReview ? 'update' : 'create');
    } catch (error) {
      // Überprüfe, ob es eine spezifische Behandlung für Längenfehler gibt
      const errorMessage = error.message.includes('600 Zeichen')
        ? error.message.replace('600 Zeichen', '900 Zeichen')
        : error.message;
        
      setErrors({
        ...errors,
        submit: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-form-container">
      <h2 className="form-title">
        <span className="material-symbols-outlined">rate_review</span>
        {editReview ? 'Bewertung bearbeiten' : 'Komponente bewerten'}
      </h2>
      
      {errors.submit && (
        <div className="error-message">
          <span className="material-symbols-outlined">error</span>
          <p>{errors.submit}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="review-form">
        <div className="form-section">
          <h3>
            <span className="material-symbols-outlined">info</span>
            Komponenten-Details
          </h3>
          
          <div className="form-group">
            <label htmlFor="manufacturer">Hersteller <span className="required">*</span></label>
            <input
              type="text"
              id="manufacturer"
              name="manufacturer"
              value={formData.manufacturer}
              onChange={handleChange}
              required
              placeholder="z.B. Dynaudio, Marantz, Pro-Ject"
            />
            {errors.manufacturer && <div className="error-message">{errors.manufacturer}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="category_id">Kategorie <span className="required">*</span></label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
            >
              <option value="">-- Kategorie wählen --</option>
              {/* Hauptkategorien */}
              {categories.filter(cat => !cat.parent_id).map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
              {/* Trennlinie zwischen Haupt- und Unterkategorien */}
              {categories.some(cat => cat.parent_id) && (
                <option disabled>──────────</option>
              )}
              {/* Unterkategorien */}
              {categories.filter(cat => cat.parent_id).map(category => {
                const parentCategory = categories.find(c => c.id === category.parent_id);
                return (
                  <option key={category.id} value={category.id}>
                    {parentCategory ? `${parentCategory.name} › ${category.name}` : category.name}
                  </option>
                );
              })}
            </select>
            {errors.category_id && <div className="error-message">{errors.category_id}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="component">Modell <span className="required">*</span></label>
            <input
              type="text"
              id="component"
              name="component"
              value={formData.component}
              onChange={handleChange}
              required
              placeholder="z.B. Contour 30i, PM-15S2, Debut Carbon"
            />
            {errors.component && <div className="error-message">{errors.component}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="hifi_chain">
              HiFi-Kette (optional)
              <span className={`char-count ${chainCharCount === MAX_CHAIN_LENGTH ? 'char-count-limit' : ''}`}>
                {chainCharCount}/{MAX_CHAIN_LENGTH}
              </span>
            </label>
            <textarea
              id="hifi_chain"
              name="hifi_chain"
              value={formData.hifi_chain}
              onChange={handleChange}
              placeholder="Kompakte Zusammenfassung Ihrer HiFi-Kette für besseren Kontext der Bewertung..."
              rows="2"
            ></textarea>
            <small className="form-hint">
              Eine kurze Zusammenfassung Ihrer HiFi-Kette hilft anderen Nutzern, Ihre Bewertung besser einzuordnen.
            </small>
            {chainCharCount === MAX_CHAIN_LENGTH && (
              <div className="char-limit-warning">
                Sie haben das Zeichenlimit erreicht.
              </div>
            )}
          </div>
        </div>
        
        <div className="form-section">
          <h3>
            <span className="material-symbols-outlined">grade</span>
            Bewertung
          </h3>
          
          <div className="form-group rating-group">
            <label>Sterne-Bewertung <span className="required">*</span></label>
            <div className="stars-container">
              {[1, 2, 3, 4, 5].map(star => (
                <span
                  key={star}
                  className={`star material-symbols-outlined ${formData.rating >= star ? 'filled' : ''}`}
                  onClick={() => handleStarClick(star)}
                >
                  star
                </span>
              ))}
            </div>
            {errors.rating && <div className="error-message">{errors.rating}</div>}
            <div className="rating-text">
              {formData.rating > 0 ? `${formData.rating} von 5 Sternen` : 'Noch nicht bewertet'}
            </div>
          </div>
          
          <div className="form-group thumbs-group">
            <label>Empfehlung <span className="required">*</span></label>
            <div className="thumbs-container">
              <button 
                type="button"
                className={`thumb-button recommend ${formData.is_recommended === true ? 'selected' : ''}`}
                onClick={() => handleThumbClick(true)}
              >
                <span className="material-symbols-outlined">thumb_up</span>
                Empfehlenswert
              </button>
              
              <button 
                type="button"
                className={`thumb-button not-recommend ${formData.is_recommended === false ? 'selected' : ''}`}
                onClick={() => handleThumbClick(false)}
              >
                <span className="material-symbols-outlined">thumb_down</span>
                Nicht empfehlenswert
              </button>
            </div>
            {errors.is_recommended && <div className="error-message">{errors.is_recommended}</div>}
          </div>
          
          {/* Neuer Preis-Leistungs-Slider */}
          <div className="form-group price-performance-group">
            <label>Preis-Leistungs-Verhältnis <span className="required">*</span></label>
            <div className="price-performance-container">
              <button 
                type="button"
                className={`price-performance-button fair ${formData.price_performance === 'FAIR' ? 'selected' : ''}`}
                onClick={() => handlePricePerformanceClick('FAIR')}
              >
                <span className="material-symbols-outlined">payments</span>
                FAIR
              </button>
              
              <button 
                type="button"
                className={`price-performance-button okay ${formData.price_performance === 'OKAY' ? 'selected' : ''}`}
                onClick={() => handlePricePerformanceClick('OKAY')}
              >
                <span className="material-symbols-outlined">balance</span>
                OKAY
              </button>
              
              <button 
                type="button"
                className={`price-performance-button expensive ${formData.price_performance === 'ÜBERTEUERT' ? 'selected' : ''}`}
                onClick={() => handlePricePerformanceClick('ÜBERTEUERT')}
              >
                <span className="material-symbols-outlined">trending_up</span>
                ÜBERTEUERT
              </button>
            </div>
            {errors.price_performance && <div className="error-message">{errors.price_performance}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="review_text">
              Bewertungstext
              <span className={`char-count ${charCount === MAX_REVIEW_LENGTH ? 'char-count-limit' : ''}`}>
                {charCount}/{MAX_REVIEW_LENGTH}
              </span>
            </label>
            <textarea
              id="review_text"
              name="review_text"
              value={formData.review_text}
              onChange={handleChange}
              placeholder="Beschreiben Sie Ihre Erfahrungen mit dieser Komponente..."
              rows="8" // Erhöht von 5 auf 8 Zeilen
              className="review-textarea" // Neue CSS-Klasse für zusätzliches Styling
            ></textarea>
            {charCount === MAX_REVIEW_LENGTH && (
              <div className="char-limit-warning">
                Sie haben das Zeichenlimit erreicht.
              </div>
            )}
            {errors.review_text && <div className="error-message">{errors.review_text}</div>}
          </div>
        </div>
        
        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <div className="loading-spinner small"></div>
                <span>Wird gespeichert...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">save</span>
                <span>{editReview ? 'Änderungen speichern' : 'Bewertung speichern'}</span>
              </>
            )}
          </button>
          
          <button 
            type="button" 
            onClick={onCancel} 
            className="cancel-button"
          >
            <span className="material-symbols-outlined">cancel</span>
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
};

export default HiFiReviewForm;